import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import { FormProps } from '@shared/components/form/types';
import { MultiSelect } from '@shared/components/multi-select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { Separator } from '@shared/components/ui/separator';
import {
  PeriodYearGroup,
  reconcilePeriodSelection,
} from '@shared/lib/period-selection';
import { cn } from '@shared/lib/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * Minimal period shape the picker needs to group months under their reporting
 * year. Satisfied both by the org `TimePeriod` (report setup) and by the
 * dynamic time-period filter options returned per table.
 */
export type PickerPeriod = {
  uuid: string;
  name: string;
  type?: string;
  parentUuid?: string | null;
  startDate?: string;
};

export type PeriodGroup = {
  heading: string;
  options: { label: string; value: string }[];
};

const startTime = (period?: PickerPeriod) =>
  period?.startDate ? new Date(period.startDate).getTime() : 0;

/**
 * Map the dynamic time-period filter options (from a table's API response)
 * into {@link PickerPeriod}s, dropping any without a uuid/name.
 */
export function toPickerPeriods(
  items?: {
    name?: string;
    uuid?: string;
    type?: string;
    parentUuid?: string | null;
    startDate?: string;
  }[]
): PickerPeriod[] {
  return (items ?? [])
    .filter((p): p is typeof p & { uuid: string; name: string } =>
      Boolean(p.uuid && p.name)
    )
    .map(({ uuid, name, type, parentUuid, startDate }) => ({
      uuid,
      name,
      type,
      parentUuid,
      startDate,
    }));
}

/** Group months under their reporting year, newest year first. */
export function usePeriodGroups(periods?: PickerPeriod[]): PeriodGroup[] {
  return useMemo(() => {
    if (!periods) return [];
    return periods
      .filter(({ type }) => type === 'year')
      .map((year) => ({
        heading: year.name,
        options: [
          { label: year.name, value: year.uuid },
          ...periods
            .filter((tp) => tp.type === 'month' && tp.parentUuid === year.uuid)
            .map((tp) => ({ label: tp.name, value: tp.uuid })),
        ],
      }))
      .sort((a, b) => {
        const yearA = periods.find((p) => p.uuid === a.options[0].value);
        const yearB = periods.find((p) => p.uuid === b.options[0].value);
        return startTime(yearB) - startTime(yearA);
      });
  }, [periods]);
}

/**
 * Reconcile a raw selection (collapse a full year of months to the year, etc.)
 * and order it chronologically. Pure — used by both the form picker and the
 * table filter.
 */
export function applyPeriodSelection(
  newValues: string[],
  prevValues: string[],
  periods?: PickerPeriod[]
): string[] {
  if (!periods) return newValues;

  const yearGroups: PeriodYearGroup[] = periods
    .filter(({ type }) => type === 'year')
    .map((year) => ({
      yearValue: year.uuid,
      monthValues: periods
        .filter((p) => p.parentUuid === year.uuid)
        .map((p) => p.uuid),
    }));

  return reconcilePeriodSelection(newValues, prevValues, yearGroups).sort(
    (a, b) => {
      const periodA = periods.find((p) => p.uuid === a);
      const periodB = periods.find((p) => p.uuid === b);
      return startTime(periodA) - startTime(periodB);
    }
  );
}

interface PeriodPickerGroupsProps {
  groups: PeriodGroup[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  onClose: () => void;
  showFooter?: boolean;
}

/**
 * Presentational dropdown body: a year header button plus a grid of its month
 * buttons, with an optional Clear all / Done footer. Shared by the form picker
 * and the table filter so both render an identical selection surface.
 */
export function PeriodPickerGroups({
  groups,
  selectedValues,
  onToggle,
  onClear,
  onClose,
  showFooter = true,
}: PeriodPickerGroupsProps) {
  return (
    <div aria-label="Time period selection">
      <div className="max-h-[25vh] space-y-2 overflow-y-auto pb-2">
        {groups.map((yearGroup) => {
          const yearOption = yearGroup.options[0];
          const months = yearGroup.options.slice(1);
          const yearSelected = selectedValues.includes(yearOption.value);
          return (
            <div key={yearGroup.heading}>
              <div className="flex flex-col items-center gap-1 p-1">
                <button
                  type="button"
                  onClick={() => onToggle(yearOption.value)}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 text-center text-sm font-medium transition-colors',
                    yearSelected
                      ? 'bg-primary-700 text-primary-foreground border-primary-700'
                      : 'bg-background hover:bg-muted'
                  )}
                  aria-pressed={yearSelected}
                >
                  {yearGroup.heading}
                </button>
                <div className="grid w-full grid-cols-3 gap-1">
                  {months.map((m) => {
                    const mSelected =
                      selectedValues.includes(m.value) || yearSelected;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => onToggle(m.value)}
                        className={cn(
                          'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                          mSelected
                            ? 'bg-primary-700 text-primary-foreground border-primary-700'
                            : 'bg-background hover:bg-muted'
                        )}
                        aria-pressed={mSelected}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showFooter && (
        <div className="border-foreground/10 flex items-center justify-between border-t bg-background p-1">
          {selectedValues.length > 0 && (
            <>
              <button
                type="button"
                onClick={onClear}
                className="max-w-full flex-1 cursor-pointer justify-center rounded-sm px-2 py-1.5 text-sm text-accent-foreground hover:bg-accent"
              >
                Clear all
              </button>
              <Separator
                orientation="vertical"
                className="flex h-full min-h-6"
              />
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="max-w-full flex-1 cursor-pointer justify-center rounded-sm px-2 py-1.5 text-sm text-accent-foreground hover:bg-accent"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

interface PeriodPickerProps {
  /** Periods to group and select from. Years (type 'year') anchor each group;
   *  months attach via `parentUuid`. */
  periods?: PickerPeriod[];
  value: string[];
  onChange: (values: string[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Controlled, presentational period picker rendered as a full-width select
 * (report setup). The period source is supplied by the caller.
 */
export function PeriodPicker({
  periods,
  value,
  onChange,
  isLoading,
  placeholder = 'All time periods included',
  className,
}: PeriodPickerProps) {
  const groups = usePeriodGroups(periods);

  return (
    <MultiSelect
      options={groups}
      isLoading={isLoading}
      onValueChange={(values: string[]) =>
        onChange(applyPeriodSelection(values, value, periods))
      }
      defaultValue={value}
      placeholder={placeholder}
      className={cn('bg-gray-00', className)}
      autoSize
      responsive
      maxWidth="100%"
      hideSelectAll
      customDropdown={({
        filteredOptions,
        selectedValues,
        toggle,
        clear,
        close,
        isGrouped,
      }) =>
        isGrouped &&
        Array.isArray(filteredOptions) &&
        filteredOptions.length > 0 &&
        'heading' in filteredOptions[0] ? (
          <PeriodPickerGroups
            groups={filteredOptions as PeriodGroup[]}
            selectedValues={selectedValues}
            onToggle={toggle}
            onClear={clear}
            onClose={close}
          />
        ) : null
      }
    />
  );
}

interface MultiPeriodPickerProps extends FormProps {}

/** React Hook Form wrapper around {@link PeriodPicker}, sourced from the
 *  organization's time periods (used on the report setup page). */
function MultiPeriodPicker({
  name,
  className,
  label,
  includeErrorMessage,
}: MultiPeriodPickerProps) {
  const { control } = useFormContext();
  const { data: periods, isLoading: isLoadingPeriods } =
    useGetOrganizationTimePeriods();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('grid flex-1 items-center', className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <PeriodPicker
              periods={periods}
              value={field.value || []}
              onChange={field.onChange}
              isLoading={isLoadingPeriods}
            />
          </FormControl>
          {includeErrorMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
}

export default MultiPeriodPicker;
